/**
 * Supabase Realtime Service
 * 
 * Provides centralized realtime subscription management using Supabase's
 * WebSocket-based realtime functionality for live updates across the app.
 */

import { supabase } from '../lib/supabase';
import { RealtimeChannel } from '@supabase/supabase-js';

/**
 * Subscribe to real-time updates for a specific game
 * Updates are triggered when breaks, frames, or game data changes
 */
export const subscribeToGameUpdates = (
  gameId: number,
  onBreakUpdate: (payload: any) => void,
  onFrameUpdate: (payload: any) => void,
  onGameUpdate: (payload: any) => void
): RealtimeChannel => {
  const channel = supabase
    .channel(`game:${gameId}`)
    .on(
      'postgres_changes',
      {
        event: '*',
        schema: 'public',
        table: 'breaks',
        filter: `gameId=eq.${gameId}`
      },
      (payload) => {
        console.log('Realtime: Break update', payload);
        onBreakUpdate(payload);
      }
    )
    .on(
      'postgres_changes',
      {
        event: '*',
        schema: 'public',
        table: 'frames',
        filter: `gameId=eq.${gameId}`
      },
      (payload) => {
        console.log('Realtime: Frame update', payload);
        onFrameUpdate(payload);
      }
    )
    .on(
      'postgres_changes',
      {
        event: 'UPDATE',
        schema: 'public',
        table: 'games',
        filter: `id=eq.${gameId}`
      },
      (payload) => {
        console.log('Realtime: Game update', payload);
        onGameUpdate(payload);
      }
    )
    .subscribe((status) => {
      console.log(`Game ${gameId} realtime subscription status:`, status);
    });

  return channel;
};

/**
 * Subscribe to real-time updates for tournament matches
 * Updates are triggered when match status, scores, or assignments change
 */
export const subscribeToTournamentMatches = (
  tournamentId: number,
  onMatchUpdate: (payload: any) => void
): RealtimeChannel => {
  const channel = supabase
    .channel(`tournament:${tournamentId}:matches`)
    .on(
      'postgres_changes',
      {
        event: '*',
        schema: 'public',
        table: 'tournament_v2_matches',
        filter: `tournament_id=eq.${tournamentId}`
      },
      (payload) => {
        console.log('Realtime: Tournament match update', payload);
        onMatchUpdate(payload);
      }
    )
    .on(
      'postgres_changes',
      {
        event: '*',
        schema: 'public',
        table: 'tournament_match_slots',
        filter: `tournament_id=eq.${tournamentId}`
      },
      (payload) => {
        console.log('Realtime: Tournament slot update', payload);
        onMatchUpdate(payload);
      }
    )
    .subscribe((status) => {
      console.log(`Tournament ${tournamentId} matches realtime subscription status:`, status);
    });

  return channel;
};

/**
 * Subscribe to real-time updates for a specific tournament match
 * Useful for detailed match view with live score updates
 */
export const subscribeToMatch = (
  matchId: number,
  onMatchUpdate: (payload: any) => void,
  onGameUpdate?: (payload: any) => void
): RealtimeChannel => {
  const channelBuilder = supabase
    .channel(`match:${matchId}`)
    .on(
      'postgres_changes',
      {
        event: '*',
        schema: 'public',
        table: 'tournament_v2_matches',
        filter: `id=eq.${matchId}`
      },
      (payload) => {
        console.log('Realtime: Match update', payload);
        onMatchUpdate(payload);
      }
    )
    .on(
      'postgres_changes',
      {
        event: '*',
        schema: 'public',
        table: 'tournament_match_slots',
        filter: `match_id=eq.${matchId}`
      },
      (payload) => {
        console.log('Realtime: Match slot update', payload);
        onMatchUpdate(payload);
      }
    );

  // If game updates callback is provided, also subscribe to game updates
  if (onGameUpdate) {
    channelBuilder.on(
      'postgres_changes',
      {
        event: '*',
        schema: 'public',
        table: 'games'
        // Note: We can't filter by match_id here, so we'll need to check in the callback
      },
      (payload) => {
        console.log('Realtime: Game update for match', payload);
        onGameUpdate(payload);
      }
    );
  }

  const channel = channelBuilder.subscribe((status) => {
    console.log(`Match ${matchId} realtime subscription status:`, status);
  });

  return channel;
};

/**
 * Subscribe to real-time updates for table devices
 * Updates when table status, assignments, or heartbeats change
 */
export const subscribeToTableDevices = (
  clubId: number,
  onTableUpdate: (payload: any) => void
): RealtimeChannel => {
  const channel = supabase
    .channel(`tables:${clubId}`)
    .on(
      'postgres_changes',
      {
        event: '*',
        schema: 'public',
        table: 'table_devices',
        filter: `club_id=eq.${clubId}`
      },
      (payload) => {
        console.log('Realtime: Table device update', payload);
        onTableUpdate(payload);
      }
    )
    .subscribe((status) => {
      console.log(`Club ${clubId} table devices realtime subscription status:`, status);
    });

  return channel;
};

/**
 * Subscribe to real-time updates for club invitations
 * Updates when invitations are created, accepted, or rejected
 */
export const subscribeToClubInvitations = (
  playerId: number,
  onInvitationUpdate: (payload: any) => void
): RealtimeChannel => {
  const channel = supabase
    .channel(`invitations:${playerId}`)
    .on(
      'postgres_changes',
      {
        event: '*',
        schema: 'public',
        table: 'player_invitations',
        filter: `player_id=eq.${playerId}`
      },
      (payload) => {
        console.log('Realtime: Invitation update', payload);
        onInvitationUpdate(payload);
      }
    )
    .subscribe((status) => {
      console.log(`Player ${playerId} invitations realtime subscription status:`, status);
    });

  return channel;
};

/**
 * Unsubscribe from a realtime channel
 * Always call this in component cleanup to prevent memory leaks
 */
export const unsubscribe = async (channel: RealtimeChannel): Promise<void> => {
  if (channel) {
    console.log('Unsubscribing from channel:', channel.topic);
    await supabase.removeChannel(channel);
  }
};

/**
 * Unsubscribe from all realtime channels
 * Useful for cleanup when unmounting main components
 */
export const unsubscribeAll = async (): Promise<void> => {
  console.log('Unsubscribing from all realtime channels');
  await supabase.removeAllChannels();
};

